import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, animate } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';

const TestimonialCarousel = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const x = useMotionValue(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/testimonials');
        const data = await response.json();
        // Filter out entries that are just images (Facebook screenshots) for the text carousel
        const textTestimonials = data.filter(item => item.content && item.content.da && item.content.da.length > 20);
        setTestimonials(textTestimonials);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (containerRef.current && testimonials.length > 0) {
      const updateMaxScroll = () => {
        const containerWidth = containerRef.current.offsetWidth;
        const scrollWidth = containerRef.current.scrollWidth;
        setMaxScroll(Math.max(0, scrollWidth - containerWidth));
      };

      updateMaxScroll();
      window.addEventListener('resize', updateMaxScroll);
      return () => window.removeEventListener('resize', updateMaxScroll);
    }
  }, [testimonials, loading]);

  const handleDragEnd = (event, info) => {
    const threshold = 50;
    if (info.offset.x > threshold) {
      prev();
    } else if (info.offset.x < -threshold) {
      next();
    }
  };

  const next = () => {
    if (currentIndex < testimonials.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setCurrentIndex(0); // Loop back to start
    }
  };

  const prev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else {
      setCurrentIndex(testimonials.length - 1); // Loop to end
    }
  };

  // Animate to current index
  useEffect(() => {
    if (containerRef.current && testimonials.length > 0) {
      const containerWidth = containerRef.current.offsetWidth;
      const cardWidth = containerWidth / (window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1);
      const targetX = -currentIndex * cardWidth;
      
      // Clamp targetX to maxScroll
      const clampedX = Math.max(-maxScroll, targetX);
      
      animate(x, clampedX, {
        type: "spring",
        stiffness: 300,
        damping: 30,
      });
    }
  }, [currentIndex, testimonials, maxScroll, x]);

  if (loading) return (
    <div className="h-96 flex flex-col items-center justify-center space-y-4 bg-[#FAF7F2]">
      <div className="w-12 h-12 border-4 border-[#E8DDCE] border-t-[#123F13] rounded-full animate-spin"></div>
      <p className="text-[#3B352F]/60 font-medium">Henter anmeldelser...</p>
    </div>
  );
  
  if (testimonials.length === 0) return null;

  return (
    <section className="py-24 bg-white overflow-hidden border-y border-[#E8DDCE]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-4">Klienternes oplevelser</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#123F13] mb-4">
              Hvad mine klienter siger
            </h2>
            <p className="text-lg text-[#3B352F]/70 leading-relaxed">
              Læs om de forandringer og resultater, som andre har opnået gennem forløb hos Core Hypnose.
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={prev}
              className="p-4 rounded-full border border-[#E8DDCE] bg-white text-[#123F13] hover:bg-[#123F13] hover:text-white transition-all focus:outline-none shadow-sm"
              aria-label="Forrige anmeldelse"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              className="p-4 rounded-full border border-[#E8DDCE] bg-white text-[#123F13] hover:bg-[#123F13] hover:text-white transition-all focus:outline-none shadow-sm"
              aria-label="Næste anmeldelse"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="relative cursor-grab active:cursor-grabbing">
          <motion.div
            ref={containerRef}
            drag="x"
            dragConstraints={{ left: -maxScroll, right: 0 }}
            style={{ x }}
            onDragEnd={handleDragEnd}
            className="flex gap-6"
          >
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="flex-shrink-0 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
              >
                <div className="bg-white rounded-2xl p-8 md:p-10 h-full flex flex-col border border-[#E8DDCE] hover:shadow-2xl hover:shadow-[#123F13]/5 transition-all duration-500 group">
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex space-x-1 text-[#B89B5E]">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          size={16}
                          className={i < (testimonial.rating || 5) ? 'fill-current' : 'text-[#E8DDCE]'} 
                        />
                      ))}
                    </div>
                    <Quote className="w-10 h-10 text-[#8C2F12]/10 group-hover:text-[#8C2F12]/20 transition-colors" />
                  </div>
                  
                  <blockquote className="flex-grow">
                    <div 
                      className="text-[#3B352F]/80 text-lg leading-relaxed italic mb-8 line-clamp-6"
                      dangerouslySetInnerHTML={{ __html: testimonial.content.da }}
                    />
                  </blockquote>

                  <div className="flex items-center pt-8 border-t border-[#FAF7F2]">
                    <div className="w-12 h-12 bg-[#123F13] rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-[#123F13]/20 mr-4 transform group-hover:scale-105 transition-transform">
                      {testimonial.name.da.charAt(0)}
                    </div>
                    <div>
                      <cite className="not-italic font-bold text-[#123F13] block text-base">
                        {testimonial.name.da}
                      </cite>
                      {testimonial.source && (
                        <span className="text-sm text-[#3B352F]/50 font-medium">
                          {testimonial.source}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Progress Bar */}
        <div className="mt-16 max-w-xs mx-auto">
          <div className="h-1 w-full bg-[#E8DDCE] rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-[#123F13]"
              initial={false}
              animate={{ 
                width: `${((currentIndex + 1) / testimonials.length) * 100}%` 
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          </div>
          <div className="flex justify-between mt-4 text-[10px] font-bold text-[#123F13]/40 uppercase tracking-[0.2em]">
            <span>01</span>
            <span>{testimonials.length < 10 ? `0${testimonials.length}` : testimonials.length}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
